import { useHomeTabs } from '../../queries/useHomeTabs';
import { TabView } from 'react-native-tab-view';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Typography, useScreenSize } from '@banhala/ui-kit';
import { useSetAtom } from 'jotai';
import { activeHomeTabAtom } from '../../states/homeTab.atom';
import type { HomeTabItem } from 'shared/src/api';
import { HomeAtomic } from '../HomeAtomic';
import { HomeWebView } from '../HomeWebView';
import { TodayAtomic } from '../TodayAtomic';
import { GoodsRanking } from '../GoodsRanking';
import { MarketRanking } from '../MarketRanking';
import { HomeTabBar } from './components/HomeTabBar';
import { isWeb } from 'shared/src/utils';

export type HomeTabRoute = {
  key: string;
  title: string;
} & HomeTabItem;

export const HomeContents = () => {
  const { data: homeTabs } = useHomeTabs();
  const initialIndex = homeTabs.item_list.findIndex(
    item => item.id === homeTabs.selected_id,
  );
  const [index, setIndex] = useState(initialIndex);
  const size = useScreenSize();
  const setActiveHomeTab = useSetAtom(activeHomeTabAtom);

  useEffect(() => {
    setActiveHomeTab(homeTabs.item_list[initialIndex]?.screen_name ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const routes = useMemo<HomeTabRoute[]>(() => {
    return (
      homeTabs.item_list?.map(item => ({
        ...item,
        key: item.id,
        title: item.name,
      })) ?? []
    );
  }, [homeTabs.item_list]);

  const handleIndexChange = useCallback(
    (newIndex: number) => {
      setIndex(newIndex);
      setActiveHomeTab(routes[newIndex]?.screen_name ?? '');
    },
    [routes, setActiveHomeTab],
  );

  if (homeTabs.item_list.length === 0) return null;

  return (
    <TabView<HomeTabRoute>
      lazy={true}
      navigationState={{ index, routes }}
      renderScene={item => {
        if (item.route.type === 'ATOMIC') {
          return (
            <HomeAtomic
              screenName={item.route.screen_name}
              nextToken={item.route.default_query?.next_token}
            />
          );
        }

        if (item.route.type === 'WEBVIEW') {
          return <HomeWebView url={item.route.default_query?.url} />;
        }

        if (item.route.type === 'CUSTOM') {
          if (item.route.screen_name === 'TODAY') {
            return <TodayAtomic screenName={item.route.screen_name} />;
          }

          if (item.route.screen_name === 'GOODS_RANKING') {
            return <GoodsRanking />;
          }

          if (item.route.screen_name === 'MARKET_RANKING') {
            return <MarketRanking />;
          }
        }
        return <Typography>{item.route.title}</Typography>;
      }}
      onIndexChange={handleIndexChange}
      initialLayout={{
        width: size.width,
      }}
      swipeEnabled={!isWeb}
      renderTabBar={props => <HomeTabBar {...props} />}
    />
  );
};
